import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline'
import { observer } from 'mobx-react-lite'
import { useMemo, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ListErrorState } from 'app/components/ai-agent-sms-ops/ListErrorState'
import { SmsAgentOpsTab } from 'app/components/ai-agent-sms-ops/SmsAgentOpsTab'
import { FAILED_REPLY_LABEL, FAILED_REPLY_STATUSES } from 'app/components/ai-agent-sms-ops/smsFailedReplies'
import { StatsRow } from 'app/components/ai-agent-voice-ops/StatsRow'
import DashboardHeader from 'app/components/analytics/DashboardHeader'
import { SectionCard } from 'app/components/SectionCard'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableCountFooter } from 'app/components/table/TableCountFooter'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import type { FailedReplyFilter } from 'app/types/sms-agent-ops'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { smsAiWorkflowColumns } from 'app/table-columns/sms-agent-ops'
import { useUser } from 'app/utils'
import { AgentLiveStatus } from './AgentLiveStatus'
import { AgentSubTabs, AgentsShell } from './AgentsShell'
import { useSmsLiveStats } from './useSmsLiveStats'

const DASHBOARD_TAB = `Dashboard`
const OPS_TAB = `Ops`
const SUPER_USER_TABS = [OPS_TAB]
const FAILED_REPLY_FILTERS: { value: FailedReplyFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...FAILED_REPLY_STATUSES.map((status) => ({ value: status, label: FAILED_REPLY_LABEL[status] })),
]

/** Every number here comes from live records; the Ops tab keeps its simulated sections. */
const SmsLiveDashboard = observer(function SmsLiveDashboard(): ReactElement {
  const { ops, store, isLoading, stats } = useSmsLiveStats()
  const workflowColumns = useMemo(() => smsAiWorkflowColumns(), [])
  const { takeovers, optOuts, optOutTotal, aiWorkflowRows, listErrors } = store

  return (
    <>
      <div className="mb-6">
        <StatsRow isLoading={isLoading} skeletonCount={4} stats={stats} />
      </div>

      <div className="flex flex-col gap-6 pb-6">
        <SectionCard
          attached
          title="By Workflow"
          info="The AI agent's texts, grouped by the workflow that handed it the thread."
          contentClassName="bg-surface gap-0"
        >
          <SimpleTable
            activeSubpage="agents-sms-workflows"
            data={aiWorkflowRows}
            columns={workflowColumns}
            initialSort={[{ id: `aiTexts`, desc: true }]}
            enableDeepLinking={false}
            hideFooter
            emptyState={
              listErrors.aiTexts ? (
                <ListErrorState subject="AI texts" />
              ) : (
                <TableEmptyState
                  icon={<ArrowPathIcon className="h-6 w-6" />}
                  title="No AI texts"
                  description="Workflows the AI agent texted for in the date range show up here."
                />
              )
            }
          />
          <TableCountFooter shown={aiWorkflowRows?.length ?? 0} total={aiWorkflowRows?.length ?? 0} noun="workflow" />
        </SectionCard>

        <SectionCard
          attached
          title="Failed AI Replies"
          info="Patient texts the AI tried to answer and could not, with the reason. A failed reply is retried automatically; Answered later means a retry or a later reply got through."
          contentClassName="bg-surface gap-0"
          headerButtons={
            <div className="max-w-full overflow-x-auto">
              <SegmentControl
                label="Status"
                buttons={FAILED_REPLY_FILTERS.map((option) => ({
                  label: `${option.label} ${ops.failedReplyCounts[option.value]}`,
                  onClick: () => ops.setFailedReplyFilter(option.value),
                  isActive: ops.failedReplyFilter === option.value,
                }))}
              />
            </div>
          }
        >
          <SimpleTable
            activeSubpage="agents-sms-failed-replies"
            data={ops.failedReplies}
            columns={ops.failedReplyColumns}
            enableDeepLinking={false}
            hideFooter
            emptyState={
              listErrors.failedReplies ? (
                <ListErrorState subject="failed AI replies" />
              ) : (
                <TableEmptyState
                  icon={<ExclamationTriangleIcon className="h-6 w-6" />}
                  title="No failed AI replies"
                  description="Every AI reply attempt in the date range went through."
                />
              )
            }
          />
          <TableCountFooter
            shown={ops.failedReplies?.length ?? 0}
            total={ops.failedReplyCounts.all}
            noun="failed reply"
            plural="failed replies"
          />
        </SectionCard>

        <SectionCard
          attached
          title="Takeovers"
          info="Each time staff took a thread over from the AI agent."
          contentClassName="bg-surface gap-0"
        >
          <SimpleTable
            activeSubpage="agents-sms-takeovers"
            data={takeovers}
            columns={ops.takeoverListColumns}
            enableDeepLinking={false}
            hideFooter
            emptyState={
              listErrors.takeovers ? (
                <ListErrorState subject="takeovers" />
              ) : (
                <TableEmptyState
                  icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
                  title="No takeovers"
                  description="Threads staff took over from the AI agent in the date range show up here."
                />
              )
            }
          />
          <TableCountFooter shown={takeovers?.length ?? 0} total={takeovers?.length ?? 0} noun="takeover" />
        </SectionCard>

        <SectionCard
          attached
          title="Opt-Outs"
          info="Numbers that opted out in the date range and are still opted out. A number that texted START again is gone."
          contentClassName="bg-surface gap-0"
        >
          <SimpleTable
            activeSubpage="agents-sms-opt-outs"
            data={optOuts}
            columns={ops.optOutColumns}
            enableDeepLinking={false}
            hideFooter
            emptyState={
              listErrors.optOuts ? (
                <ListErrorState subject="opt-outs" />
              ) : (
                <TableEmptyState
                  icon={<NoSymbolIcon className="h-6 w-6" />}
                  title="No opt-outs"
                  description="Numbers that opt out by STOP, a form, or staff show up here."
                />
              )
            }
          />
          <TableCountFooter shown={optOuts?.length ?? 0} total={optOutTotal ?? 0} noun="opt-out" />
        </SectionCard>
      </div>
    </>
  )
})

/** Agents → SMS once the agent runs: live records under the same header as Analytics → SMS Agent. */
export function AgentsSmsDashboard(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSuper = useUser()?.type === `super`
  const smsLocations = useLocationIntegrationStatus([], `SMS Agent`, true)
  const openTab = isSuper && searchParams.get(`tab`) === OPS_TAB ? OPS_TAB : DASHBOARD_TAB

  return (
    <AgentsShell
      header={
        <DashboardHeader
          title="Agents"
          noticeClassName="mb-6"
          hideCompare={true}
          hideChannelSelector={true}
          hideGroupedLocationOptions
          hideLocationSelector={smsLocations.isLoading || smsLocations.locationOptions.length <= 1}
          locationProduct="SMS Agent"
          requireProductInUse
          hideUniqueNewPatientsToggle={true}
          defaultDateRange="this_month"
          integrationWarningSpacing
        />
      }
    >
      <AgentSubTabs
        value={openTab}
        onChange={(tab) =>
          setSearchParams(
            (prev) => {
              prev.set(`tab`, tab)
              return prev
            },
            { replace: true },
          )
        }
        tabs={isSuper ? [DASHBOARD_TAB, ...SUPER_USER_TABS] : [DASHBOARD_TAB]}
        superUserTabs={SUPER_USER_TABS}
        trailing={<AgentLiveStatus kind="sms" />}
      />
      {openTab === OPS_TAB ? <SmsAgentOpsTab /> : <SmsLiveDashboard />}
    </AgentsShell>
  )
}
